import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Input, Select, Field } from '@/components/ui/Field';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { MarkdownField } from '@/components/forms/MarkdownField';
import { slugify } from '@/lib/utils';
import type { RoleRow } from '@/lib/admin-queries';

const TYPES = ['full-time', 'part-time', 'contract', 'intern'] as const;
const STATUSES = ['draft', 'published', 'archived'] as const;

const schema = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/),
  title: z.string().min(2),
  type: z.enum(TYPES),
  status_publish: z.enum(STATUSES),
});

interface Props {
  value: RoleRow;
  submitting: boolean;
  onSubmit: (v: RoleRow) => void | Promise<void>;
  onCancel: () => void;
}

interface BulletEditorProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}

function BulletEditor({ label, items, onChange, placeholder }: BulletEditorProps) {
  const [input, setInput] = useState('');
  function add() {
    const v = input.trim();
    if (!v) return;
    if (!items.includes(v)) onChange([...items, v]);
    setInput('');
  }
  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-fg-muted">{label}</label>
      <ul className="space-y-1.5">
        {items.map((it, idx) => (
          <li key={`${it}-${idx}`} className="flex items-start gap-2 rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" />
            <span className="flex-1">{it}</span>
            <button
              type="button"
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-xs text-fg-muted hover:text-danger"
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Input
          placeholder={placeholder ?? 'Add an item'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="secondary" onClick={add}>
          Add
        </Button>
      </div>
    </div>
  );
}

export function RoleEditForm({ value, submitting, onSubmit, onCancel }: Props) {
  const [v, setV] = useState<RoleRow>(value);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function patch<K extends keyof RoleRow>(key: K, val: RoleRow[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onTitleBlur() {
    if (!v.slug) patch('slug', slugify(v.title));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const parsed = schema.safeParse(v);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    onSubmit(v);
  }

  return (
    <form id="role-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Field id="title" label="Title" required error={errors.title}>
          {({ id, invalid }) => (
            <Input id={id} required invalid={invalid} value={v.title} onChange={(e) => patch('title', e.target.value)} onBlur={onTitleBlur} />
          )}
        </Field>
        <Field id="slug" label="Slug" required error={errors.slug}>
          {({ id, invalid }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              value={v.slug}
              onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            />
          )}
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Field id="department" label="Department">
          {({ id }) => (
            <Input id={id} value={v.department ?? ''} onChange={(e) => patch('department', e.target.value)} />
          )}
        </Field>
        <Field id="location" label="Location">
          {({ id }) => <Input id={id} value={v.location ?? ''} onChange={(e) => patch('location', e.target.value)} />}
        </Field>
        <Field id="type" label="Type" required>
          {({ id }) => (
            <Select id={id} value={v.type ?? 'full-time'} onChange={(e) => patch('type', e.target.value as RoleRow['type'])}>
              {TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field id="status_publish" label="Publish" required>
          {({ id }) => (
            <Select id={id} value={v.status_publish} onChange={(e) => patch('status_publish', e.target.value as RoleRow['status_publish'])}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <Field id="apply_email" label="Apply email" hint="Defaults to careers@vonlof.co. Notifications go here.">
        {({ id }) => (
          <Input id={id} type="email" value={v.apply_email ?? ''} onChange={(e) => patch('apply_email', e.target.value)} />
        )}
      </Field>

      <Field id="description_md" label="Description" hint="Markdown. Shown above responsibilities.">
        <MarkdownField value={v.description_md ?? ''} onChange={(s) => patch('description_md', s)} rows={10} />
      </Field>

      <BulletEditor
        label="Responsibilities"
        items={v.responsibilities}
        onChange={(items) => patch('responsibilities', items)}
        placeholder="What you'll do"
      />
      <BulletEditor
        label="Requirements"
        items={v.requirements}
        onChange={(items) => patch('requirements', items)}
        placeholder="What you'll bring"
      />
      <BulletEditor
        label="Nice to have"
        items={v.nice_to_haves}
        onChange={(items) => patch('nice_to_haves', items)}
        placeholder="Bonus skills"
      />

      <label className="mt-6 flex items-center gap-2 text-sm">
        <Switch checked={v.is_open} onCheckedChange={(c) => patch('is_open', !!c)} id="is_open" />
        <span>Accepting applications</span>
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="role-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Create role'}
        </Button>
      </div>
    </form>
  );
}